/*
 * Settings, in this order: account → package → data and backup → display → help and about → delete data.
 * Only choices that work are shown; plans that are not on sale say so and have no pay button.
 */
import { dmyhm } from "../domain/format";
import { hasSample, totals } from "../domain/library";
import { FREE_ACTION_LIMIT, PLANS, type Plan } from "../domain/plans";
import { addSampleData, removeSampleData } from "../sample/sample-data";
import { ctx } from "./context";
import { ic } from "./icons";
import { downloadBackup, openExportText, openImportSheet, refreshSafety, safety, undoLastImport } from "./import-export";
import { head, openSheet, toast } from "./shell";
import { failNotice, failOf } from "./failure";
import { failReasonOf } from "../storage/errors";
import { accountSettingsHTML, localOfferSettingsHTML } from "./account";
import { gridPrefs, setGridPref, topBar } from "./app";
import { startBlankLibrary } from "./welcome";

/** two-tap confirm used by destructive buttons, same pattern as the prototype's delete */
function armed(el: HTMLElement, label: string): boolean {
  if (el.classList.contains("arm")) return true;
  el.classList.add("arm"); el.innerHTML = ic("trash") + label; return false;
}

const baht = (n: number) => n.toLocaleString("th-TH");
const sec = (id: string, icon: string, title: string, body: string) =>
  `<section class="set" id="${id}"><h2>${ic(icon)}${title}</h2>${body}</section>`;

/** own records vs sample records, counted from the loaded library (Settings opens only after it is loaded) */
function counts() {
  const S = ctx.store.lib;
  const sampleActions = S.actions.filter(a => a.sample).length;
  const sample = S.workspaces.filter(x => x.sample).length + S.categories.filter(x => x.sample).length + sampleActions;
  return { t: totals(S), ownActions: S.actions.length - sampleActions, sampleActions, sample };
}

function accountSection(): string {
  if (ctx.mode === "cloud") return sec("set-account", "user", "บัญชี", accountSettingsHTML());
  const { store } = ctx;
  return sec("set-account", "user", "บัญชี", `<div class="panel"><dl class="kv">
      <dt>บัญชี</dt><dd id="acct-email">ไม่ได้เข้าสู่ระบบ</dd>
      <dt>ข้อมูลอยู่ที่</dt><dd id="set-store">เบราว์เซอร์นี้เท่านั้น ไม่ซิงก์ไปเครื่องอื่น</dd>
      <dt>การบันทึก</dt><dd id="acct-sync">${store.storageOK
        ? `<span class="tag okk">บันทึกได้</span><span class="sync-text">ข้อมูลยังอยู่หลัง refresh ในเบราว์เซอร์นี้ แต่หายถ้าล้างข้อมูลเว็บหรือเปิดโหมดส่วนตัว ส่งออกไฟล์สำรองไว้เสมอ</span>`
        : `<span class="tag bad">บันทึกไม่ได้</span><span class="sync-text">เบราว์เซอร์นี้ไม่อนุญาตให้บันทึก ข้อมูลจะหายเมื่อปิดหน้า</span>`}</dd>
    </dl></div>`);
}

function planSection(): string {
  const c = counts();
  const over = c.ownActions > FREE_ACTION_LIMIT;
  return sec("set-plan", "sparkles", "แพ็กเกจ", `<div class="panel"><dl class="kv">
      <dt>ตอนนี้</dt><dd id="plan-now"><span class="tag">ช่วงทดลอง Beta</span><span class="sync-text">Beta ยังไม่จำกัด Action และยังไม่มีการเก็บเงิน ใช้ได้ทุกอย่างที่มีในแอป</span></dd>
      <dt>ใช้อยู่</dt><dd id="plan-usage"><b>${c.ownActions}</b> Action${c.sampleActions ? ` <span class="sub">(ไม่นับตัวอย่าง ${c.sampleActions})</span>` : ""}
        <span class="sub" style="display:block">แพ็กเกจ Free จะรับได้ ${FREE_ACTION_LIMIT} Action รวมทั้งบัญชี${over ? " · ตอนนี้เกินแล้ว แต่ยังใช้ได้ปกติในช่วง Beta" : ""}</span></dd>
    </dl></div>
    <button class="btn" data-do="plans" id="btn-plans" style="width:100%;margin-top:10px">${ic("info")}ดูแพ็กเกจ: ซื้ออะไร ได้อะไร</button>`);
}

function dataSection(): string {
  const { store } = ctx, S = store.lib, c = counts(), cloud = ctx.mode === "cloud";
  return sec("set-backup", "folder", "ข้อมูลและการสำรอง", `
    <div class="panel"><dl class="kv"><dt>ตอนนี้มี</dt><dd id="set-counts">${c.t.workspaces} สถานที่ · ${c.t.categories} หมวด · ${c.t.actions} Action</dd></dl></div>
    <div class="sub-set">
      <h3>ส่งออก (สำรอง)</h3>
      <p>เก็บทุกสถานที่ หมวด และ Action เป็นไฟล์ไว้กับตัว ใช้กู้คืนหรือย้ายไปเครื่อง/บัญชีอื่น</p>
      <div class="stack">
        <button class="btn primary" data-do="export" id="btn-export">${ic("ext")}ส่งออกไฟล์สำรอง</button>
        <button class="btn" data-do="export-text" id="btn-export-text">${ic("copy")}คัดลอกเป็นข้อความ</button>
      </div>
    </div>
    <div class="sub-set">
      <h3>นำเข้า (กู้คืน)</h3>
      <p>นำไฟล์สำรองกลับเข้ามา ระบบสรุปให้ดูก่อนยืนยันทุกครั้ง รายการที่มีอยู่แล้วไม่ถูกเพิ่มซ้ำ และไม่ถูกเขียนทับถ้าไม่ได้เลือก</p>
      <div class="stack">
        <button class="btn" data-do="import" id="btn-import">${ic("plus")}นำเข้าไฟล์สำรอง</button>
        ${safety ? `<button class="btn ghost" data-do="undo-import" id="btn-undo-import">${ic("left")}ย้อนกลับเป็นข้อมูล${safety.reason} (${dmyhm(safety.savedAt)})</button>` : ""}
      </div>
    </div>
    ${localOfferSettingsHTML()}
    <div class="sub-set sample-set" id="set-sample">
      <h3>${ic("sparkles")}ข้อมูลตัวอย่าง <span class="tag">แยกจากข้อมูลจริง</span></h3>
      ${hasSample(S)
        ? `<p>มีรายการตัวอย่าง ${c.sample} รายการ ลบได้โดยไม่กระทบรายการที่คุณสร้างเอง</p><button class="btn danger" data-do="sample-remove" id="btn-sample-remove" style="width:100%">${ic("trash")}ลบเฉพาะข้อมูลตัวอย่าง</button>`
        : `<p>ไม่มีข้อมูลตัวอย่าง${cloud ? "ในบัญชีนี้" : "ในเครื่องนี้"} เพิ่มได้เพื่อลองใช้งาน รายการของคุณจะไม่ถูกแตะ</p><button class="btn" data-do="sample-add" id="btn-sample-add" style="width:100%">${ic("plus")}เพิ่มข้อมูลตัวอย่าง</button>`}
    </div>`);
}

function displaySection(): string {
  const g = gridPrefs();
  const seg = (which: string, label: string, opts: [boolean, string][], on: boolean) =>
    `<div class="pref"><span class="pref-l">${label}</span><div class="seg" role="group" aria-label="${label}">${opts.map(([v, t]) =>
      `<button type="button" data-do="pref" data-w="${which}" data-v="${v ? 1 : 0}" id="pref-${which}-${v ? 1 : 0}" aria-pressed="${v === on}">${t}</button>`).join("")}</div></div>`;
  return sec("set-display", "grid", "การแสดงผล", `<p>ตั้งแยกในแต่ละเครื่อง ไม่ซิงก์ไปเครื่องอื่น</p>
    <div class="panel">
      ${seg("folders", "หมวดในหน้าแรก", [[false, "2 คอลัมน์"], [true, "3 คอลัมน์"]], g.folders3)}
      ${seg("actions", "Action ในหมวด", [[false, "3 คอลัมน์"], [true, "4 คอลัมน์"]], g.dense)}
      <div class="pref"><span class="pref-l">เปิดลิงก์</span><span class="pref-v">แท็บใหม่ในเบราว์เซอร์</span></div>
    </div>`);
}

function helpSection(): string {
  const v = import.meta.env.VITE_APP_VERSION as string, commit = import.meta.env.VITE_BUILD_COMMIT as string;
  return sec("set-help", "info", "ช่วยเหลือและเกี่ยวกับ", `<div class="stack">
      <button class="btn" data-do="help" id="btn-help">${ic("info")}วิธีใช้ MEMOLINK</button>
    </div>
    <div class="panel" style="margin-top:10px"><dl class="kv">
      <dt>เวอร์ชัน</dt><dd id="app-version">${v} Beta${commit ? ` · ${commit}` : ""}</dd>
      <dt>เว็บไซต์</dt><dd><a class="lnk" href="https://memolink.app" target="_blank" rel="noopener">memolink.app</a></dd>
    </dl></div>`);
}

function wipeSection(): string {
  const cloud = ctx.mode === "cloud";
  return sec("set-wipe", "trash", "การลบข้อมูล", `<p>ลบทุกอย่าง${cloud ? "ในบัญชีนี้ (ทุกเครื่อง)" : "ในเบราว์เซอร์นี้"}แล้วกลับไปหน้าเริ่มต้น ระบบเก็บสำเนาไว้ย้อนกลับได้ 1 ครั้ง แต่ควรส่งออกไฟล์สำรองก่อน</p>
    <button class="btn danger" data-do="wipe" id="btn-wipe" style="width:100%">${ic("trash")}ล้างข้อมูลทั้งหมด</button>`);
}

export function renderSettings($app: HTMLElement) {
  $app.innerHTML = topBar() + `<h1 class="page-title">ตั้งค่า</h1>`
    + accountSection() + planSection() + dataSection() + displaySection() + helpSection() + wipeSection();
}

/* ---------- package details ---------- */

function planCard(p: Plan): string {
  const price = p.monthly === null ? `<span class="price">ฟรี</span>`
    : `<span class="price">${baht(p.monthly)} บาท<small>/เดือน</small></span><span class="price-alt">หรือ ${baht(p.yearly!)} บาท/ปี</span>`;
  const inc = `<ul class="plan-inc">${p.includes.map(x => x.ready
    ? `<li>${ic("check")}<span>${x.text}</span></li>`
    : `<li class="later">${ic("info")}<span>${x.text} <span class="tag">ยังไม่พร้อม</span></span></li>`).join("")}</ul>`;
  return `<div class="plan" id="plan-${p.id}"><div class="plan-head"><h3>${p.name}</h3><span class="tag">${p.badge}</span></div>
    <div class="plan-price">${price}</div>${inc}</div>`;
}

export function renderPlans($app: HTMLElement) {
  const c = counts();
  $app.innerHTML = topBar() + `<h1 class="page-title">แพ็กเกจ</h1>
    <p class="lead">ซื้ออะไร ได้อะไร</p>
    <section class="set" id="plans-now"><h2>${ic("check")}ใช้ได้ตอนนี้ใน Beta</h2>
      <div class="panel"><ul class="plan-inc pad">
        <li>${ic("check")}<span><b>ยังไม่จำกัด Action</b> สร้างสถานที่ หมวด และ Action ได้ไม่จำกัด (ตอนนี้ใช้ ${c.ownActions} Action)</span></li>
        ${ctx.mode === "cloud" ? `<li>${ic("check")}<span>ซิงก์ข้อมูลกับบัญชี เปิดได้หลายเครื่อง</span></li>` : ""}
        <li>${ic("check")}<span>ส่งออกและนำเข้าไฟล์สำรอง</span></li>
        <li>${ic("check")}<span>ไอคอนชุด Classic และประเภทหมวดทั้งหมด</span></li>
        <li>${ic("check")}<span>ไม่มีการเก็บเงิน</span></li>
      </ul></div></section>
    <section class="set" id="plans-later"><h2>${ic("sparkles")}แพ็กเกจหลังจบ Beta</h2>
      <div class="acct-banner" id="plans-status" role="status"><span><b>ยังไม่เปิดขาย</b> ยังไม่มีการชำระเงินและยังไม่บังคับจำนวน Action ราคาด้านล่างเป็นราคาที่วางไว้สำหรับตอนเปิดขาย</span></div>
      <div class="panel plans" id="plans-list">${PLANS.map(planCard).join("")}</div></section>`;
}

/* ---------- help ---------- */

const HELP: [string, string][] = [
  ["MEMOLINK เก็บอะไร", "ลิงก์และโน้ตของคุณ จัดเป็น 3 ชั้น: สถานที่ (แท็บด้านบน) › หมวด › Action"],
  ["เปิดลิงก์ / อ่านโน้ต", "แตะไอคอน Action ลิงก์เปิดในแท็บใหม่ โน้ตเปิดให้อ่านและคัดลอก ปุ่มแชร์แยกไว้ที่มุมไอคอน"],
  ["เพิ่มหรือแก้", "แตะปุ่มดินสอด้านบนเพื่อเข้าโหมดแก้ไข แล้วแตะรายการที่ต้องการ หรือปุ่ม + เพื่อเพิ่ม"],
  ["กันข้อมูลหาย", "ตั้งค่า › ข้อมูลและการสำรอง › ส่งออกไฟล์สำรอง เก็บไฟล์ไว้ นำเข้าคืนได้โดยไม่เกิดรายการซ้ำ"],
  ["ใช้หลายเครื่อง", "เข้าสู่ระบบด้วยอีเมลเดียวกันทุกเครื่อง ถ้าแก้จากอีกเครื่อง กดโหลดข้อมูลล่าสุดหรือ refresh"]
];
function openHelp() {
  openSheet(head("วิธีใช้ MEMOLINK", ic("info")) + `<dl class="help" id="help-list">${HELP.map(([q, a]) => `<dt>${q}</dt><dd>${a}</dd>`).join("")}</dl>
    <div class="stack" style="margin-top:14px"><button class="btn" data-do="close">ปิด</button></div>`);
}

/* ---------- clicks ---------- */

export function handleSettingsAction(act: string, el: HTMLElement): boolean {
  const { store, V } = ctx;
  switch (act) {
    case "export": downloadBackup(); return true;
    case "export-text": openExportText(); return true;
    case "import": openImportSheet(); return true;
    case "plans": V.view = "plans"; ctx.render(); scrollTo(0, 0); return true;
    case "help": openHelp(); return true;
    case "pref": setGridPref(el.dataset.w === "folders" ? "folders" : "actions", el.dataset.v === "1"); ctx.render(); toast("ใช้กับเครื่องนี้แล้ว"); return true;
    case "undo-import":
      if (!armed(el, "แตะอีกครั้งเพื่อย้อนกลับ (ข้อมูลตอนนี้จะถูกแทนที่)")) return true;
      void undoLastImport(); return true;
    case "sample-add":
      void store.replace(addSampleData(store.lib)).then(r => { ctx.render(); if (r.ok) toast("เพิ่มข้อมูลตัวอย่างแล้ว"); else failNotice(r); });
      return true;
    case "sample-remove": {
      if (!armed(el, "แตะอีกครั้งเพื่อลบข้อมูลตัวอย่าง")) return true;
      const r = removeSampleData(store.lib);
      const next = r.library.workspaces.length ? r.library : startBlankLibrary(r.library);
      void store.replace(next).then(res => { ctx.render(); if (!res.ok) { failNotice(res); return; } toast(r.kept ? `ลบตัวอย่างแล้ว เก็บ ${r.kept} หมวด/สถานที่ที่มีรายการของคุณไว้` : "ลบข้อมูลตัวอย่างแล้ว"); });
      return true; }
    case "wipe":
      if (!armed(el, "แตะอีกครั้งเพื่อยืนยันล้างข้อมูล")) return true;
      void (async () => {
        try { await store.repo.saveSafetyCopy(store.lib, "ก่อนล้างข้อมูลทั้งหมด"); }
        catch (e) {
          ctx.render();
          if (failReasonOf(e) === "storage") toast("ยังไม่ล้าง: เก็บสำเนาไว้ย้อนกลับไม่ได้ ข้อมูลยังอยู่ครบ"); else failNotice(failOf(e));
          return;
        }
        const res = await store.clearAll();
        if (!res.ok) { ctx.render(); if (res.reason === "storage") toast("ล้างข้อมูลไม่ได้ เบราว์เซอร์ปฏิเสธ ข้อมูลยังอยู่ครบ"); else failNotice(res); return; }
        await refreshSafety();
        V.view = "welcome"; V.catId = null; ctx.render(); scrollTo(0, 0); toast("ล้างข้อมูลแล้ว");
      })();
      return true;
  }
  return false;
}
